import { messages } from "./messages";
import { isVatClauseChanged, willResetMval96 } from "./mval96";
const MESSAGE_ORDER = {
    error: 0,
    warning: 1,
    info: 2
};
export const byMessageSeverity = (a, b) => MESSAGE_ORDER[a.type] - MESSAGE_ORDER[b.type];
const regNoSearchMessages = {
    failed: messages.regNoSearchFailed,
    generalError: messages.regNoSearchGeneralError,
    notFound: messages.regNoSearchNotFound,
    multipleResults: messages.regNoSearchMultipleResults,
    objectTypeMappingMissing: messages.regNoSearchObjectTypeMappingMissing
};
const chassisNoSearchMessages = {
    failed: messages.chassisNoSearchFailed,
    generalError: messages.chassisNoSearchGeneralError,
    notFound: messages.chassisNoSearchNotFound,
    multipleResults: messages.chassisNoSearchMultipleResults,
    objectTypeMappingMissing: messages.chassisNoSearchObjectTypeMappingMissing
};
const searchError = (intl, error, value, errorMessages) => {
    if (error === undefined)
        return intl.formatMessage(errorMessages.failed, { value });
    switch (error) {
        case "GeneralError":
            return intl.formatMessage(errorMessages.generalError, { value });
        case "NotFound":
            return intl.formatMessage(errorMessages.notFound, { value });
        case "MultipleResults":
            return intl.formatMessage(errorMessages.multipleResults, { value });
        case "ObjectTypeMappingMissing":
            return intl.formatMessage(errorMessages.objectTypeMappingMissing, {
                value
            });
        default:
            return error;
    }
};
export const searchErrorText = (intl, error, regNo) => searchError(intl, error, regNo, regNoSearchMessages);
export const chassisSearchErrorText = (intl, error, chassisNo) => searchError(intl, error, chassisNo, chassisNoSearchMessages);
const showLeasingDescriptionWarning = (searchResult, currentObject) => {
    return Boolean(currentObject?.application?.hasStartedSigning) && currentObject?.application?.productTypeId === 2 && searchResult?.object != null && (currentObject?.object?.make !== searchResult?.object?.make ||
        currentObject?.object?.model !== searchResult?.object?.model ||
        currentObject?.object?.engine !== searchResult?.object?.engine ||
        currentObject?.object?.year !== searchResult?.object?.year);
};
const showLoanEquityWarning = (searchResult, currentObject) => {
    return Boolean(currentObject?.application?.hasStartedSigning) && currentObject?.application?.productTypeId === 1 && searchResult?.object != null;
};
export const regNoWarnings = (intl, options) => {
    const { searchResult, currentObject, regNoEmpty, regNoSearchMissing, searchValidationDisabled, regNoNotValidated, docsWillReset, docsWillResetOnSave, regNoChanged, searchedRegNo, regNoInput, canResetDocs, hasSearchError } = options;
    if (currentObject?.application?.isLocked) {
        return [
            {
                id: "formLocked",
                text: intl.formatMessage(messages.formLocked),
                type: "warning"
            }
        ];
    }
    if (regNoEmpty) {
        return [
            {
                id: "regNoEmpty",
                text: intl.formatMessage(messages.regNoEmpty),
                type: "error"
            }
        ];
    }
    const objectTypeWrong = searchResult
        ? searchResult.mustReplace ?? false
        : !regNoSearchMissing && (currentObject?.mustReplace ?? false);
    const result = [];
    if (objectTypeWrong) {
        if (!currentObject?.application?.canReplace) {
            if (regNoChanged) {
                result.push({
                    id: "replace",
                    text: intl.formatMessage(messages.replaceObjectCannotReplaceIfSaved, { regNo: searchedRegNo }),
                    type: "error"
                });
            }
            else {
                result.push({
                    id: "replace",
                    text: intl.formatMessage(messages.replaceObjectCannotReplace, { regNo: currentObject?.object?.regNo }),
                    type: "error"
                });
            }
        }
        else if (regNoChanged) {
            result.push({
                id: "replace",
                text: intl.formatMessage(messages.replaceObjectIfSaved, { regNo: searchedRegNo }),
                type: "error"
            });
        }
        else {
            result.push({
                id: "replace",
                text: intl.formatMessage(messages.replaceObject, { regNo: currentObject?.object?.regNo }),
                type: "error"
            });
        }
    }
    if (showLeasingDescriptionWarning(searchResult, currentObject)) {
        result.push({
            id: "leasingDescriptionWarning",
            text: intl.formatMessage(messages.warnLeasingDescription),
            type: "info"
        });
    }
    else if (showLoanEquityWarning(searchResult, currentObject)) {
        result.push({
            id: "loanEquityWarning",
            text: intl.formatMessage(messages.warnLoanEquity),
            type: "info"
        });
    }
    if (regNoSearchMissing && searchValidationDisabled) {
        result.push({
            id: "regNoNotSearched",
            text: intl.formatMessage(messages.warnRegNoNotSearchedInfo, { regNo: regNoInput }),
            type: "warning"
        }, {
            id: "regNoSearchValidationDisabled",
            text: intl.formatMessage(messages.warnSearchValidationDisabled),
            type: "info"
        });
    }
    else if (regNoSearchMissing && !hasSearchError) {
        result.push({
            id: "regNoNotSearched",
            text: intl.formatMessage(messages.warnRegNoMustSearch, { regNo: regNoInput }),
            type: "warning"
        });
    }
    else if (regNoNotValidated) {
        result.push({
            id: "regNoNotSearched",
            text: intl.formatMessage(messages.warnRegNoNotValidated, { regNo: searchedRegNo }),
            type: "warning"
        });
    }
    if (!canResetDocs && docsWillResetOnSave) {
        result.push({
            id: "docsWillReset",
            text: intl.formatMessage(messages.errorDocsWillBeReset),
            type: "error"
        });
    }
    else if (docsWillReset) {
        if (currentObject?.application?.isSecurityRegistered) {
            result.push({
                id: "securityRegistered",
                text: intl.formatMessage(messages.objectIsSecurityRegistered, { regNo: currentObject.object?.regNo }),
                type: regNoSearchMissing ? "warning" : "error"
            });
        }
        if (willResetMval96(searchResult?.mismatch)) {
            result.push({
                id: "docsWillResetMval96",
                text: intl.formatMessage(messages.warnDocsWillBeResetMval96, { regNo: searchedRegNo }),
                type: "error"
            });
        }
        else if (isVatClauseChanged(searchResult?.mismatch)) {
            result.push({
                id: "docsWillResetVatClause",
                text: intl.formatMessage(messages.warnDocsWillBeResetVatClause, { regNo: searchedRegNo }),
                type: "error"
            });
        }
        result.push({
            id: "docsWillReset",
            text: intl.formatMessage(messages.warnDocsWillBeReset),
            type: "info"
        });
    }
    currentObject?.messages?.forEach((message) => {
        result.push(message);
    });
    searchResult?.messages?.forEach((message) => {
        result.push(message);
    });
    return result;
};
//# sourceMappingURL=encumberanceCheckMessages.js.map